const BASE = (import.meta as any).env?.PUBLIC_API_BASE || "/api";

let accessToken: string | null = null;
let refreshToken: string | null = null;
try { accessToken = localStorage.getItem("ztb_access"); } catch {}
try { refreshToken = localStorage.getItem("ztb_refresh"); } catch {}

const authBus = new EventTarget();
export function onAuthChange(cb: (token: string | null) => void) {
  const h = (e: Event) => cb((e as CustomEvent<string | null>).detail);
  authBus.addEventListener("change", h);
  return () => authBus.removeEventListener("change", h);
}

export function setAuth(token: string | null, refresh?: string | null) {
  accessToken = token;
  // refresh 显式传 null/字符串才覆盖；undefined 表示保持旧值
  if (refresh !== undefined) refreshToken = refresh;
  try {
    if (token) localStorage.setItem("ztb_access", token);
    else localStorage.removeItem("ztb_access");
    if (refresh !== undefined) {
      if (refresh) localStorage.setItem("ztb_refresh", refresh);
      else localStorage.removeItem("ztb_refresh");
    }
    if (token === null && refresh === undefined) {
      // 登出：连带清掉 refresh
      localStorage.removeItem("ztb_refresh");
      refreshToken = null;
    }
  } catch {}
  authBus.dispatchEvent(new CustomEvent("change", { detail: token }));
}
export function getAuth() { return accessToken; }

// 单飞：避免并发 401 触发多次 refresh
let refreshing: Promise<string | null> | null = null;
async function tryRefresh(): Promise<string | null> {
  if (!refreshToken) return null;
  if (refreshing) return refreshing;
  refreshing = (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!res.ok) {
        setAuth(null, null);
        return null;
      }
      const data = await res.json();
      setAuth(data.access_token, data.refresh_token);
      return data.access_token as string;
    } catch {
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

export async function api<T = any>(path: string, init: RequestInit = {}): Promise<T> {
  const send = async (token: string | null) => {
    const headers = new Headers(init.headers);
    if (!headers.has("Content-Type") && init.body) headers.set("Content-Type", "application/json");
    if (token) headers.set("Authorization", `Bearer ${token}`);
    return fetch(`${BASE}${path}`, { ...init, headers });
  };
  let res = await send(accessToken);
  if (res.status === 401 && refreshToken && !path.startsWith("/auth/")) {
    const t = await tryRefresh();
    if (t) res = await send(t);
  }
  if (!res.ok) {
    let err: any = null;
    try { err = await res.json(); } catch {}
    throw Object.assign(new Error(err?.message || `HTTP ${res.status}`), { status: res.status, body: err });
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

export const Public = {
  siteConfig: () => api<Record<string, any>>("/site/config"),
  siteStats: () => api<{ total: number; winners: number; today: number; categories: number }>("/site/stats"),
  welcomeGift: () => api<{
    enabled: boolean; vip_days: number; points: number;
    hero_title: string; hero_message: string; cta_label: string;
  }>("/site/welcome-gift"),
  categories: () => api<any[]>("/categories"),
  categoryFacets: (slug: string) => api<{
    bidders: Array<{ name: string; count: number }>;
    years: Array<{ year: number; count: number }>;
    provinces: Array<{ name: string; count: number }>;
  }>(`/categories/${encodeURIComponent(slug)}/facets`),
  projects: (q: Record<string, string | number | undefined>) => {
    const sp = new URLSearchParams();
    Object.entries(q).forEach(([k, v]) => { if (v !== undefined && v !== "") sp.set(k, String(v)); });
    return api<{ items: any[]; page: number; page_size: number }>(`/projects?${sp}`);
  },
  project: (slug: string) => api<any>(`/projects/${encodeURIComponent(slug)}`),
};

export const Auth = {
  captcha: () => api<{ id: string; image: string }>("/auth/captcha/image"),
  loginConfig: () => api<{ captcha: "none" | "image" | "turnstile" | "geetest"; site_key?: string; captcha_id?: string }>("/auth/login/config"),
  registerConfig: () => api<{
    enabled: boolean;
    captcha: "none" | "image" | "turnstile" | "geetest";
    site_key?: string;
    captcha_id?: string;
    verify_email: boolean;
    verify_sms: boolean;
    allow_email: boolean;
    allow_phone: boolean;
  }>("/auth/register/config"),
  sendEmailCode: (email: string) => api("/auth/captcha/email", { method: "POST", body: JSON.stringify({ email }) }),
  sendSmsCode: (phone: string) => api("/auth/captcha/sms", { method: "POST", body: JSON.stringify({ phone }) }),
  login: (payload: {
    email: string; password: string;
    captcha_id?: string; captcha_code?: string;
    turnstile_token?: string;
    geetest?: { lot_number: string; captcha_output: string; pass_token: string; gen_time: string };
  }) =>
    api<{ access_token: string; refresh_token: string }>("/auth/login", {
      method: "POST", body: JSON.stringify(payload),
    }),
  register: (payload: any) =>
    api("/auth/register", { method: "POST", body: JSON.stringify(payload) }),
  resetRequest: (payload: { channel: "email" | "sms"; target: string; captcha_id: string; captcha_code: string }) =>
    api("/auth/password/reset/request", { method: "POST", body: JSON.stringify(payload) }),
  resetConfirm: (payload: { channel: "email" | "sms"; target: string; code: string; new_password: string }) =>
    api("/auth/password/reset/confirm", { method: "POST", body: JSON.stringify(payload) }),
};

export const Me = {
  profile: () => api<any>("/me"),
  updateProfile: (body: any) => api("/me/profile", { method: "PUT", body: JSON.stringify(body) }),
  orders: () => api<any[]>("/orders"),
  changePassword: (old_password: string, new_password: string) =>
    api("/me/password", { method: "PUT", body: JSON.stringify({ old_password, new_password }) }),
  bindEmailRequest: (email: string) =>
    api("/me/bind/email/request", { method: "POST", body: JSON.stringify({ email }) }),
  bindEmailVerify: (email: string, code: string) =>
    api("/me/bind/email/verify", { method: "POST", body: JSON.stringify({ email, code }) }),
  bindPhoneRequest: (phone: string) =>
    api("/me/bind/phone/request", { method: "POST", body: JSON.stringify({ phone }) }),
  bindPhoneVerify: (phone: string, code: string) =>
    api("/me/bind/phone/verify", { method: "POST", body: JSON.stringify({ phone, code }) }),
  viewHistory: (page = 1, page_size = 20) =>
    api<{ items: any[]; total: number }>(`/me/history/views?page=${page}&page_size=${page_size}`),
  downloadHistory: (page = 1, page_size = 20) =>
    api<{ items: any[]; total: number }>(`/me/history/downloads?page=${page}&page_size=${page_size}`),
};

export const Wallet = {
  get: () => api<{ points: number; updated_at: string }>("/wallet"),
  transactions: (page = 1, page_size = 20) =>
    api<{ items: any[]; total: number }>(`/wallet/transactions?page=${page}&page_size=${page_size}`),
  purchases: (page = 1, page_size = 20) =>
    api<{ items: any[]; total: number }>(`/wallet/purchases?page=${page}&page_size=${page_size}`),
  packages: () => api<any[]>("/wallet/packages"),
  purchaseProject: (project_id: string) =>
    api<{ ok: boolean; already?: boolean; delta?: number; balance?: number }>(`/wallet/purchase/${project_id}`, { method: "POST" }),
};

export const Orders = {
  plans: () => api<any[]>("/orders/plans"),
  create: (body: { kind?: "membership" | "points"; plan_code?: string; package_code?: string; provider: "stripe" | "alipay" | "wechat" }) =>
    api<{ order_id: string; redirect_url?: string; qr_code_url?: string }>("/orders", {
      method: "POST", body: JSON.stringify(body),
    }),
};

// ====== 签到 ======
export const Checkin = {
  status: () => api<any>("/checkin/status"),
  do: () => api<{ ok: boolean; streak: number; points_award: number; base_points: number; bonus: number }>("/checkin", { method: "POST" }),
};

// ====== 公告（站点滚动条 / 弹窗 / 侧栏） ======
export const Announcements = {
  list: (placement: "marquee" | "popup" | "sidebar", visitor_token?: string) => {
    const sp = new URLSearchParams({ placement });
    if (visitor_token) sp.set("visitor_token", visitor_token);
    return api<{ items: any[] }>(`/announcements?${sp}`);
  },
  dismiss: (id: number, visitor_token?: string) =>
    api(`/announcements/${id}/dismiss`, { method: "POST", body: JSON.stringify({ visitor_token }) }),
};

// ====== 兑换码 ======
export const Redeem = {
  redeem: (code: string) => api<{ ok: boolean; reward_type: string; reward: any; summary: string }>("/redeem", { method: "POST", body: JSON.stringify({ code }) }),
};

// ====== 订阅推送 ======
export type SubscriptionRule = {
  id: string; name: string;
  category_id: string | null;
  category_slug: string | null;
  category_name: string | null;
  keyword: string;
  match_fields: string[];
  channel_ids: string[];
  is_active: boolean;
  last_matched_at: string | null;
  hit_count: number;
  created_at: string;
};
export type NotifyChannel = {
  id: string; kind: "email" | "webhook" | "sms";
  target: string;
  is_active: boolean;
  verified_at: string | null;
  created_at: string;
};
export type SubscriptionConfig = {
  enabled: boolean;
  points_per_push: number;
  max_rules_per_user: number;
  require_active_vip: boolean;
  active_vip: boolean;
  rule_count: number;
  can_subscribe: boolean;
};
export const Subscriptions = {
  config: () => api<SubscriptionConfig>("/me/subscriptions/config"),
  channels: () => api<NotifyChannel[]>("/me/subscriptions/channels"),
  createChannel: (kind: "email", target: string) =>
    api<{ id: string; verified: boolean; needs_verify: boolean }>("/me/subscriptions/channels",
      { method: "POST", body: JSON.stringify({ kind, target }) }),
  deleteChannel: (id: string) =>
    api(`/me/subscriptions/channels/${id}`, { method: "DELETE" }),
  requestChannelVerify: (id: string) =>
    api(`/me/subscriptions/channels/${id}/verify/request`, { method: "POST" }),
  confirmChannelVerify: (id: string, code: string) =>
    api(`/me/subscriptions/channels/${id}/verify/confirm`,
      { method: "POST", body: JSON.stringify({ code }) }),
  rules: () => api<SubscriptionRule[]>("/me/subscriptions/rules"),
  createRule: (body: Partial<SubscriptionRule> & { keyword: string }) =>
    api<{ id: string }>("/me/subscriptions/rules",
      { method: "POST", body: JSON.stringify(body) }),
  updateRule: (id: string, body: Partial<SubscriptionRule> & { keyword: string }) =>
    api(`/me/subscriptions/rules/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  deleteRule: (id: string) =>
    api(`/me/subscriptions/rules/${id}`, { method: "DELETE" }),
  outbox: (page = 1, page_size = 20) =>
    api<{ items: any[]; total: number }>(`/me/subscriptions/outbox?page=${page}&page_size=${page_size}`),
  check: (keyword: string, category_id?: string) => {
    const qs = new URLSearchParams({ keyword });
    if (category_id) qs.set("category_id", category_id);
    return api<{ rule_id: string | null; can_subscribe: boolean; rules_used: number; rules_limit: number }>(
      `/me/subscriptions/check?${qs}`);
  },
  adminRetryOutbox: (id: number | string) =>
    api(`/me/subscriptions/admin/outbox/${id}/retry`, { method: "POST" }),
};

export type InboxItem = {
  id: number;
  kind: string;
  title: string;
  body: string;
  ref_type: string | null;
  ref_id: string | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
};
export const Inbox = {
  list: (page = 1, page_size = 20, unread_only = false) =>
    api<{ items: InboxItem[]; total: number; unread: number; page: number; page_size: number }>(
      `/me/inbox?page=${page}&page_size=${page_size}&unread_only=${unread_only}`),
  unreadCount: () => api<{ unread: number }>("/me/inbox/unread-count"),
  markRead: (id: number) => api(`/me/inbox/${id}/read`, { method: "POST" }),
  markAllRead: () => api("/me/inbox/read-all", { method: "POST" }),
  remove: (id: number) => api(`/me/inbox/${id}`, { method: "DELETE" }),
};
export const AdminInbox = {
  send: (body: {
    scope: "user" | "role" | "all";
    user_id?: string;
    email?: string;
    role?: string;
    kind?: string;
    title: string;
    body?: string;
    also_email?: boolean;
  }) => api<{ ok: boolean; recipients: number; sent: number }>(
    "/admin/inbox/send", { method: "POST", body: JSON.stringify(body) }),
};

export const Admin = {
  // 栏目
  categories: () => api<any[]>("/admin/categories"),
  saveCategory: (id: string | null, body: any) =>
    api(id ? `/admin/categories/${id}` : "/admin/categories",
        { method: id ? "PUT" : "POST", body: JSON.stringify(body) }),
  delCategory: (id: string) => api(`/admin/categories/${id}`, { method: "DELETE" }),

  // 项目
  projects: (q: Record<string, any>) => {
    const sp = new URLSearchParams();
    Object.entries(q).forEach(([k, v]) => { if (v != null && v !== "") sp.set(k, String(v)); });
    return api<{ items: any[]; page: number; page_size: number; total: number }>(`/admin/projects?${sp}`);
  },
  saveProject: (id: string, body: any) => api(`/admin/projects/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  delProject: (id: string) => api(`/admin/projects/${id}`, { method: "DELETE" }),

  // 付费规则
  rules: () => api<any[]>("/admin/paywall-rules"),
  saveRule: (id: number | null, body: any) =>
    api(id ? `/admin/paywall-rules/${id}` : "/admin/paywall-rules",
        { method: id ? "PUT" : "POST", body: JSON.stringify(body) }),
  delRule: (id: number) => api(`/admin/paywall-rules/${id}`, { method: "DELETE" }),

  // 套餐
  plans: () => api<any[]>("/admin/membership-plans"),
  savePlan: (id: number | null, body: any) =>
    api(id ? `/admin/membership-plans/${id}` : "/admin/membership-plans",
        { method: id ? "PUT" : "POST", body: JSON.stringify(body) }),
  delPlan: (id: number) => api(`/admin/membership-plans/${id}`, { method: "DELETE" }),

  // 积分套餐
  pointPackages: () => api<any[]>("/admin/point-packages"),
  savePointPackage: (id: number | null, body: any) =>
    api(id ? `/admin/point-packages/${id}` : "/admin/point-packages",
        { method: id ? "PUT" : "POST", body: JSON.stringify(body) }),
  delPointPackage: (id: number) => api(`/admin/point-packages/${id}`, { method: "DELETE" }),

  // 公告
  announcements: () => api<any[]>("/admin/announcements"),
  saveAnnouncement: (id: number | null, body: any) =>
    api(id ? `/admin/announcements/${id}` : "/admin/announcements",
        { method: id ? "PUT" : "POST", body: JSON.stringify(body) }),
  delAnnouncement: (id: number) => api(`/admin/announcements/${id}`, { method: "DELETE" }),

  // 兑换码
  redeemCodes: () => api<any[]>("/admin/redeem-codes"),
  createRedeemCodes: (body: any) => api<{ count: number; items: { id: number; code: string }[] }>("/admin/redeem-codes", { method: "POST", body: JSON.stringify(body) }),
  updateRedeemCode: (id: number, body: any) => api(`/admin/redeem-codes/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  delRedeemCode: (id: number) => api(`/admin/redeem-codes/${id}`, { method: "DELETE" }),

  // API Key
  apiKeys: () => api<any[]>("/admin/api-keys"),
  createApiKey: (body: any) => api<{ id: string; key: string }>("/admin/api-keys", { method: "POST", body: JSON.stringify(body) }),
  delApiKey: (id: string) => api(`/admin/api-keys/${id}`, { method: "DELETE" }),
  reissueApiKey: (id: string) => api<{ id: string; key: string }>(`/admin/api-keys/${id}/reissue`, { method: "POST" }),

  // 用户
  users: (q: Record<string, any>) => {
    const sp = new URLSearchParams();
    Object.entries(q).forEach(([k, v]) => { if (v != null && v !== "") sp.set(k, String(v)); });
    return api<{ items: any[]; page: number; page_size: number; total: number }>(`/admin/users?${sp}`);
  },
  setUserRole: (id: string, role: string, action: "add" | "remove") =>
    api(`/admin/users/${id}/role`, { method: "POST", body: JSON.stringify({ role, action }) }),

  // 站点设置
  getSetting: (key: string) => api<{ key: string; value: any }>(`/admin/site-settings/${encodeURIComponent(key)}`),
  setSetting: (key: string, value: any) =>
    api(`/admin/site-settings/${encodeURIComponent(key)}`, { method: "PUT", body: JSON.stringify(value) }),
  setMaintenance: (enabled: boolean, message?: string) =>
    api(`/admin/maintenance`, { method: "POST", body: JSON.stringify({ enabled, message }) }),

  // 分析
  analyticsSummary: () => api<any>("/admin/analytics/summary"),
  analyticsYearly: () => api<any[]>("/admin/analytics/yearly"),
  analyticsByProvince: () => api<any[]>("/admin/analytics/by-province"),
  analyticsTopBidders: () => api<any[]>("/admin/analytics/top-bidders"),
};
